import { useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { getFeedbackData, putModelFeedbackData } from 'common/api';
import { IFeedback, IObjectData, IWorkFlowFeedback } from 'common/api/type-def';
import FeedbackIconDisplay from 'common/components/FeedbackIconDisplay/FeedbackIconDisplay';
import FeedbackPopper from 'common/components/FeedbackPopper/FeedbackPopper';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	APP_GENERAL_ERROR_MSG,
	FEEDBACK_API_ERROR_MSG,
} from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { setIsAISummaryRegenerated } from 'common/store/reducer/horizonScanReducer';
import { setRegInvSummaryRegenerated } from 'common/store/reducer/regulationInventoryReducer';
import {
	addWorkflowFeedback,
	setWorkflowFeedback,
} from 'common/store/reducer/simulationReducer';
import { renderFeedbackSuccess } from 'common/utils/utilFunctions';

import styles from './AIFeedback.module.scss';

interface IAIFeedbackProps {
	objectType: string;
	module: string;
	objectData: IObjectData;
	shouldResetFeedback?: boolean;
	isDisabled?: boolean;
	allowSaveObjectType?: boolean;
}

const { OK } = HTTP_STATUS_CODES;

const AIFeedback: React.FC<IAIFeedbackProps> = (props: IAIFeedbackProps) => {
	const {
		objectType,
		module,
		objectData,
		isDisabled,
		shouldResetFeedback,
		allowSaveObjectType = false,
	} = props;
	const [feedbackInfo, setFeedbackInfo] = useState<any>(null);
	const [feedBackClicked, setFeedBackClicked] = useState<boolean>(false);
	const [showFeedbackSuccess, setShowFeedbackSuccess] =
		useState<boolean>(false);
	const [isLike, setIsLike] = useState<boolean | undefined>();
	const workflowFeedback = useSelector(
		(state: RootState) => state.simulationArena.workflowFeedback,
	);

	const [summaryFeedbackAnchorEl, setSummaryFeedbackAnchorEl] =
		useState<null | HTMLElement>(null);
	const spanRef = useRef() as React.MutableRefObject<HTMLDivElement>;

	const emptyFeedback = {
		comment: '',
		isLike,
		id: '',
		reason: '',
	};
	const dispatch = useDispatch();
	const handleClickSummaryFeedback = () => {
		setSummaryFeedbackAnchorEl(spanRef.current ? spanRef?.current : null);
	};

	const clickLikeFeedBackIcon = () => {
		if (isLike) {
			setFeedBackClicked(false);
			setIsLike(undefined);
		} else {
			setFeedBackClicked(true);
			setIsLike(true);
		}
		handleClickSummaryFeedback();
	};

	const clickUnLikeFeedBackIcon = () => {
		if (isLike ?? true) {
			setFeedBackClicked(true);
			setIsLike(false);
		} else {
			setFeedBackClicked(false);
			setIsLike(undefined);
		}
		handleClickSummaryFeedback();
	};

	const submitObligationSummaryFeedback = async (feedbackData: any) => {
		const newFeedbackArray = [...workflowFeedback];

		const foundIndex = newFeedbackArray.findIndex(
			(e) =>
				feedbackData.objectType && e.workflowStep === feedbackData.objectType,
		);
		if (foundIndex !== -1) {
			const oldFeedback = newFeedbackArray[foundIndex];
			if (!feedbackData?.objectData?.reference) {
				newFeedbackArray[foundIndex] = {
					...oldFeedback,
					regenerate: true,
					newFeedback: feedbackData,
				};
			} else {
				newFeedbackArray[foundIndex] = {
					...oldFeedback,
					...(oldFeedback.regenerate
						? { newFeedback: feedbackData }
						: { existingFeedback: feedbackData }),
				};
			}
		} else {
			const workflowStep: IWorkFlowFeedback = {
				regenerate: !feedbackData?.objectData?.reference,
				workflowStep: objectType,
				...(!feedbackData?.objectData?.reference
					? { newFeedback: feedbackData, existingFeedback: null }
					: { existingFeedback: feedbackData, newFeedback: null }),
			};
			newFeedbackArray.push(workflowStep);
		}
		dispatch(setWorkflowFeedback(newFeedbackArray));
		setFeedbackInfo(feedbackData);
		setShowFeedbackSuccess(true);
		setTimeout(() => {
			setShowFeedbackSuccess(false);
		}, 6000);
		setIsLike(undefined);
		setFeedBackClicked(false);
	};

	const cancelObligationSummaryFeedback = () => {
		setFeedBackClicked(false);
		setIsLike(undefined);
	};

	useEffect(() => {
		setFeedbackInfo(null);
		setShowFeedbackSuccess(false);
		if (objectType && objectData?.reference) {
			const feedback = checkFeebackExists();
			if (feedback?.workflowStep) {
				setFeedbackInfo(
					feedback.regenerate
						? feedback.newFeedback
						: feedback.existingFeedback,
				);
			} else {
				getFeedbackandStore(objectData?.reference);
			}
		} else {
			getFeedbackFromStore();
		}
	}, [objectData?.reference, objectType]);

	const revertFeedbackReference = async (feedbackObj: IFeedback) => {
		const updatedFeedback = removeReferenceObject(feedbackObj);
		const response = await putModelFeedbackData(
			feedbackObj.id,
			updatedFeedback,
		);
		if (response.statusCode === OK) {
			dispatch(setRegInvSummaryRegenerated(false));
			dispatch(setIsAISummaryRegenerated(false));
			setFeedbackInfo(null);
			setIsLike(undefined);
			setShowFeedbackSuccess(false);
		}
	};

	const resetFeedbackInfo = async () => {
		const foundIndex = workflowFeedback.findIndex(
			(e) => e.workflowStep === objectType,
		);
		if (foundIndex !== -1) {
			const newFeedbackArray = [...workflowFeedback];
			const resetObj = newFeedbackArray[foundIndex];
			if (allowSaveObjectType) {
				const workflowStep: IWorkFlowFeedback = {
					...resetObj,
					regenerate: true,
					newFeedback: null,
					existingFeedback: removeReferenceObject(resetObj?.existingFeedback),
				};
				newFeedbackArray[foundIndex] = workflowStep;
				dispatch(setWorkflowFeedback(newFeedbackArray));
			} else if (resetObj.existingFeedback) {
				revertFeedbackReference(resetObj.existingFeedback);
			}
		}
	};

	const removeReferenceObject = (feedback: IFeedback | null) => {
		let updatedFeedback: IFeedback | null = null;
		if (feedback) {
			const updatedObjectData = { ...feedback?.objectData };
			delete updatedObjectData.reference;
			updatedFeedback = {
				...feedback,
				object_data: { ...updatedObjectData },
			};
		}
		return updatedFeedback;
	};

	const getFeedbackandStore = async (id: string) => {
		const feedback = await getFeedbackInfo(id);
		if (feedback) {
			const workflowStep: IWorkFlowFeedback = {
				regenerate: false,
				workflowStep: objectType,
				newFeedback: null,
				existingFeedback: feedback.objectType ? feedback : null,
			};
			dispatch(addWorkflowFeedback(workflowStep));
			setFeedbackInfo(feedback);
		}
	};

	const getFeedbackInfo = async (id: string) => {
		let updatedFeedback: IFeedback = {} as IFeedback;
		try {
			const response = await getFeedbackData(objectType, id);
			if (response.statusCode === OK) {
				if (
					Array.isArray(response.responseBody) &&
					response.responseBody.length > 0
				) {
					const responseData = response.responseBody;
					responseData.sort((a, b) => {
						const dateA: number = new Date(a.lastUpdated).getTime();
						const dateB: number = new Date(b.lastUpdated).getTime();
						return dateB - dateA;
					});
					const data = responseData[0];
					updatedFeedback = data;
				}
			} else {
				dispatch(
					setToastMessage({
						message: APP_GENERAL_ERROR_MSG,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			}
		} catch (error) {
			dispatch(
				setToastMessage({
					message: FEEDBACK_API_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
		return updatedFeedback;
	};

	const checkFeebackExists = () => {
		const foundIndex = workflowFeedback.findIndex(
			(e) => e.workflowStep === objectType,
		);
		if (foundIndex !== -1) {
			return workflowFeedback[foundIndex];
		}
		return {} as IWorkFlowFeedback;
	};

	const getFeedbackFromStore = async () => {
		const feedback = checkFeebackExists();
		if (feedback?.newFeedback) {
			setFeedbackInfo(feedback.newFeedback);
		}
	};

	const removeFeedbackFromStore = () => {
		const newWorkflowArray: IWorkFlowFeedback[] = [];

		workflowFeedback.forEach((ele) => {
			if (ele.workflowStep !== objectType) {
				newWorkflowArray.push(ele);
			}
		});

		if (workflowFeedback.length !== newWorkflowArray.length) {
			dispatch(setWorkflowFeedback([...newWorkflowArray]));
		}
	};
	const getObjectData = () => {
		if (allowSaveObjectType) {
			const feedback = checkFeebackExists();
			if (feedback?.regenerate) {
				return {
					...objectData,
					reference: '',
				};
			}
		}
		return objectData;
	};
	useEffect(() => {
		if (shouldResetFeedback) {
			if (objectType && objectData?.reference) {
				resetFeedbackInfo();
			} else {
				removeFeedbackFromStore();
			}
			setFeedbackInfo(null);
			setIsLike(undefined);
			setShowFeedbackSuccess(false);
		}
	}, [shouldResetFeedback]);

	return (
		<div className={styles['submitted-text-wrapper']}>
			{renderFeedbackSuccess(showFeedbackSuccess, styles['submitted-text'])}
			<div ref={spanRef} className={styles['chat-feedback-grid']}>
				<FeedbackIconDisplay
					previousFeedBack={feedbackInfo || emptyFeedback}
					currentFeedBack={emptyFeedback}
					clickLikeFeedBack={clickLikeFeedBackIcon}
					clickUnLikeFeedBack={clickUnLikeFeedBackIcon}
					disabled={isDisabled}
				/>
				<FeedbackPopper
					setAnchorEl={setSummaryFeedbackAnchorEl}
					objectType={objectType}
					module={module}
					anchorEl={summaryFeedbackAnchorEl}
					isLike={isLike}
					feedBackClicked={feedBackClicked}
					feedbackInfo={feedbackInfo}
					objectData={getObjectData()}
					emptyResponse={emptyFeedback}
					handleClickAway={cancelObligationSummaryFeedback}
					submitFeedback={submitObligationSummaryFeedback}
					cancelFeedback={cancelObligationSummaryFeedback}
				/>
			</div>
		</div>
	);
};
export default AIFeedback;
